import { useState } from "react";
import Modal from "./modal";
import Spinner from "./spinner";
import ReactSwitch from "react-switch";
import PRODUCTS from "../api/services/PRODUCTS";
import { toast } from "react-toastify";
import { BASE_URL } from "../config";
import styles from "../styles/_components/createProductModal.module.scss";

const CreateProductModal = ({ setIsOpen, setResponseStatus, existingProduct }) => {
  const isEdit = !!existingProduct;

  const [name, setName] = useState(existingProduct?.name || "");
  const [description, setDescription] = useState(existingProduct?.description || "");
  const [label, setLabel] = useState(existingProduct?.label || "");
  const [price, setPrice] = useState(existingProduct?.price?.toString() || "");
  const [salePrice, setSalePrice] = useState(existingProduct?.salePrice?.toString() || "");
  const [amountToPay, setAmountToPay] = useState(existingProduct?.amountToPay?.toString() || "");
  const [quantityAmount, setQuantityAmount] = useState(existingProduct?.quantityAmount?.toString() || "");
  const [active, setActive] = useState(existingProduct?.active ?? true);
  const [purchasable, setPurchasable] = useState(existingProduct?.purchasable ?? true);
  const [subscription, setSubscription] = useState(existingProduct?.subscription ?? false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(
    existingProduct?.image ? `${BASE_URL}/uploads/${existingProduct.image}` : null
  );
  const [loading, setLoading] = useState(false);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error("יש להזין שם מוצר");
      return;
    }
    if (!amountToPay) {
      toast.error("יש להזין סכום לתשלום");
      return;
    }

    const formData = new FormData();
    formData.append("name", name);
    formData.append("description", description);
    if (label) formData.append("label", label);
    if (price) formData.append("price", price);
    if (salePrice) formData.append("salePrice", salePrice);
    formData.append("amountToPay", amountToPay);
    if (quantityAmount) formData.append("quantityAmount", quantityAmount);
    formData.append("active", active);
    formData.append("purchasable", purchasable);
    formData.append("subscription", subscription);
    if (imageFile) {
      formData.append("product", imageFile);
    }

    try {
      setLoading(true);
      let res;
      if (isEdit) {
        res = await PRODUCTS.edit(existingProduct._id, formData);
      } else {
        res = await PRODUCTS.create(formData);
      }
      toast.success(res.data?.message || (isEdit ? "המוצר עודכן בהצלחה!" : "המוצר נוסף בהצלחה!"));
      setIsOpen(false);
      setResponseStatus(true);
    } catch (error) {
      console.log(error);
      toast.error(error?.response?.data?.message || "שגיאה בשמירת המוצר");
    } finally {
      setLoading(false);
    }
  };

  const switchProps = {
    checkedHandleIcon: false,
    uncheckedHandleIcon: false,
    checkedIcon: false,
    uncheckedIcon: false,
    onHandleColor: "#ffffff",
    offHandleColor: "#ffffff",
    onColor: "#d00254",
    offColor: "#d3d3d3",
  };

  return (
    <Modal setIsOpen={setIsOpen} onClick={() => null} type={1}>
      <div className={styles.container} dir="rtl">
        {/* Image upload */}
        <div className={styles.imageUpload}>
          {imagePreview ? (
            <img src={imagePreview} alt="תמונת מוצר" />
          ) : (
            <p>לחץ להעלאת תמונה</p>
          )}
          <input type="file" accept="image/*" onChange={handleImageChange} />
        </div>

        {/* Name */}
        <div className={styles.field}>
          <label>שם המוצר *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="שם המוצר"
          />
        </div>

        {/* Description */}
        <div className={styles.field}>
          <label>תיאור</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="תיאור קצר של המוצר"
          />
        </div>

        {/* Label */}
        <div className={styles.field}>
          <label>תווית / קטגוריה</label>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="תווית לסינון או קטגוריה"
          />
        </div>

        {/* Price + Sale Price */}
        <div className={styles.row}>
          <div className={styles.field}>
            <label>מחיר</label>
            <input
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="₪0"
            />
          </div>
          <div className={styles.field}>
            <label>מחיר מבצע</label>
            <input
              type="number"
              value={salePrice}
              onChange={(e) => setSalePrice(e.target.value)}
              placeholder="₪0 (אופציונלי)"
            />
          </div>
        </div>

        {/* Amount to pay + Quantity */}
        <div className={styles.row}>
          <div className={styles.field}>
            <label>כמות לתשלום *</label>
            <input
              type="number"
              value={amountToPay}
              onChange={(e) => setAmountToPay(e.target.value)}
              placeholder="₪0"
            />
          </div>
          <div className={styles.field}>
            <label>כמות במלאי</label>
            <input
              type="number"
              value={quantityAmount}
              onChange={(e) => setQuantityAmount(e.target.value)}
              placeholder="כמות (אופציונלי)"
            />
          </div>
        </div>

        {/* Toggles */}
        <div className={styles.switchRow}>
          <div className={styles.switchItem}>
            <ReactSwitch
              checked={active}
              onChange={setActive}
              {...switchProps}
            />
            <p>מוצר פעיל</p>
          </div>
          <div className={styles.switchItem}>
            <ReactSwitch
              checked={purchasable}
              onChange={setPurchasable}
              {...switchProps}
            />
            <p>ניתן לרכישה</p>
          </div>
          <div className={styles.switchItem}>
            <ReactSwitch
              checked={subscription}
              onChange={setSubscription}
              {...switchProps}
            />
            <p>מנוי</p>
          </div>
        </div>

        <button
          onClick={handleSubmit}
          disabled={!name.trim() || !amountToPay || loading}
          className={styles.button}
        >
          {loading ? <Spinner /> : isEdit ? "עדכון מוצר" : "הוספת מוצר"}
        </button>
      </div>
    </Modal>
  );
};

export default CreateProductModal;
